import React, { useEffect, useState } from "react";

import { mdiContentSave } from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import AdminUserAPI from "../../api/AdminUserAPI";
import "../../css/admin/admin-user-form.scss";
import AdminUser from "../../models/AdminUser";
import { validatePasswordRules } from "../../utils/data/User";
import Button, { BUTTON_SIZES, BUTTON_TYPES } from "../button/Button";
import PasswordValidator from "../common/form/PasswordValidator";
import history from "../common/navigation/AppHistory";
import SwitchOnOff from "../common/tools/SwitchOnOff";
import ProfilePicture from "../form/ProfilePicture";
import TextField from "../form/TextField";
import { ADMIN_USERS_ROUTE } from "../routes/routes";

import type { ChangeEvent, FormEvent, JSX } from "react";

import type { AdminUserToCreate } from "../../api/AdminUserAPI.types";
import type { AppState } from "../../reducers";

const PASSWORD_RULES = ["noSpaces", "min8", "lowercase", "uppercase", "digits"];

interface SingleAdminFormProps {
  user: AdminUserToCreate | AdminUser;
}

function SingleAdminForm({ user }: SingleAdminFormProps): JSX.Element {
  const { t } = useTranslation("editor-users");

  const isNewUser = !user.uuid;

  const [stateUser, setStateUser] = useState(user);

  const loggedInUser = useSelector(
    (state: AppState) => state.fetchReducer.user?.data,
  );

  useEffect(() => {
    if (user?.uuid !== stateUser?.uuid) {
      setStateUser(user);
    }
  }, [user, stateUser?.uuid]);

  const showSuccessMessage = (): void => {
    toast.success(isNewUser ? t("userCreated") : t("userUpdated"));
  };

  const showErrorMessage = (): void => {
    toast.error(t("saveError"));
  };

  const queryClient = useQueryClient();

  const updateUser = useMutation({
    mutationFn: async (updatedUser: AdminUser | AdminUserToCreate) =>
      AdminUserAPI.updateAdminUser(updatedUser),
    onMutate: async (updatedUser) => {
      if (isNewUser) {
        return null;
      }

      const { uuid: userId } = updatedUser;

      const getAdminUserQueryKey = AdminUserAPI.generateQuery({
        fn: AdminUserAPI.getSingleAdminUser,
        params: [userId],
      }).queryKey;

      await queryClient.cancelQueries(getAdminUserQueryKey);

      const previousUserInfos = queryClient.getQueryData(getAdminUserQueryKey);

      const newUserInfos = (updatedUser as AdminUser).toAPI();

      queryClient.setQueryData(getAdminUserQueryKey, newUserInfos);

      return { previousUserInfos };
    },
    onSuccess: (updatedUser) => {
      const getAdminUserListQueryKey = AdminUserAPI.generateQuery({
        fn: AdminUserAPI.getAdminUserList,
        params: [],
      }).queryKey;

      queryClient.invalidateQueries({ queryKey: getAdminUserListQueryKey });
      showSuccessMessage();
      if (isNewUser) {
        history.push(`${ADMIN_USERS_ROUTE}/${updatedUser.uuid}`);
      }
    },
    onError: () => showErrorMessage(),
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement>,
    status: ChangeEvent<HTMLInputElement>,
  ): void => {
    const { name, value } = status ? status.target : e.target; // handles legacy switch

    const updatedUser = new AdminUser({
      // eslint-disable-next-line @typescript-eslint/no-misused-spread
      ...stateUser,
      [name]: value,
    });

    setStateUser(updatedUser);
  };

  const handleAvatarChange = (avatarUrl: string): void => {
    const updatedUser = new AdminUser({
      // eslint-disable-next-line @typescript-eslint/no-misused-spread
      ...stateUser,
      avatarUrl,
    });

    setStateUser(updatedUser);
  };

  const handleUserStatusChange = (enabled: boolean): void => {
    if (stateUser instanceof AdminUser) {
      const updatedUser = stateUser.setIsEnabled(enabled);
      setStateUser(updatedUser);
    }
  };

  const isFormValid = isNewUser
    ? validatePasswordRules(stateUser.password, PASSWORD_RULES)
    : true;

  const handleSave = (): void => {
    if (isFormValid) {
      updateUser.mutate(stateUser);
    }
  };

  const preventSubmitEvent = (submitEvent: FormEvent<HTMLFormElement>): void =>
    submitEvent.preventDefault();

  const isEnabled =
    stateUser instanceof AdminUser ? stateUser.isEnabled : false;

  const isLoggedInUser = loggedInUser?.uuid === stateUser.uuid;

  const isOwner = stateUser instanceof AdminUser && stateUser.isOwner;

  const isReadOnly = isOwner && !isLoggedInUser;

  return (
    <div className="admin-user-form">
      <form className="form-container" onSubmit={preventSubmitEvent}>
        <div className="admin-user-form_container">
          <div className="flex-ver">
            <ProfilePicture
              isDisabled={isReadOnly}
              setUserAvatar={handleAvatarChange}
              userAvatar={stateUser.avatarUrl}
            />
            <div className="admin-user-switches">
              <div className="admin-user-switches_status">
                <span>{t("status")}</span>
                <SwitchOnOff
                  checked={isEnabled}
                  disabled={isOwner}
                  name="userStatus"
                  onChange={handleUserStatusChange}
                  size="sm"
                />
              </div>
            </div>
          </div>
          <div className="flex-ver admin-user-form_text-fields">
            <div className="flex-hor">
              <TextField
                required
                className="admin-user-form_text-field"
                isDisabled={isReadOnly}
                label={t("firstName")}
                name="firstName"
                onChange={handleChange}
                value={stateUser.firstName}
              />
              <TextField
                required
                className="admin-user-form_text-field"
                isDisabled={isReadOnly}
                label={t("lastName")}
                name="lastName"
                onChange={handleChange}
                value={stateUser.lastName}
              />
            </div>
            <div className="flex-hor">
              <TextField
                className="admin-user-form_text-field"
                isDisabled={isOwner}
                label={t("email")}
                name="email"
                onChange={handleChange}
                required={!isOwner}
                type="email"
                value={stateUser.email}
              />
              <TextField
                className="admin-user-form_text-field"
                isDisabled={isOwner}
                label={t("company")}
                name="company"
                onChange={handleChange}
                required={!isOwner}
                value={stateUser.company}
              />
            </div>
            {isNewUser ? (
              <React.Fragment>
                <div className="flex-hor">
                  <TextField
                    required
                    autocomplete="new-password"
                    className="admin-user-form_text-field"
                    label={t("setPassword")}
                    name="password"
                    onChange={handleChange}
                    type="password"
                    value={stateUser.password}
                  />
                </div>
                <div className="admin-user-form_password-validator-container">
                  <PasswordValidator
                    password={stateUser.password ?? " "}
                    rules={PASSWORD_RULES}
                  />
                </div>
              </React.Fragment>
            ) : null}
          </div>
        </div>
        <div className="admin-user-form_submit">
          <Button
            isDisabled={!isFormValid || isReadOnly}
            onClick={handleSave}
            size={BUTTON_SIZES.MD}
            type={BUTTON_TYPES.SUBMIT}
          >
            <div className="flex items-center justify-center gap-[0.46875rem]">
              <Icon path={mdiContentSave} size={0.9375} />{" "}
              {t("editor-utils:save")}
            </div>
          </Button>
        </div>
      </form>
    </div>
  );
}

export default SingleAdminForm;
